import React, { useRef, useEffect } from 'react';
import { View, StyleSheet } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  useAnimatedProps, useAnimatedStyle, useDerivedValue, useSharedValue,
} from 'react-native-reanimated';
import Svg, { Ellipse, Polygon } from 'react-native-svg';
import PressableStyled from '../PressableStyled';
import MoveIcon from '../16pxIcons/MoveIcon';
import Colors from '../../constants/Colors';
import Style from '../../constants/Style';

const AnimatedPolygon = Animated.createAnimatedComponent(Polygon);
const AnimatedEllipse = Animated.createAnimatedComponent(Ellipse);

const DrawRectGrab = (props) => {
  const isEllipse: boolean = props.selectedTool === 'ellipse';

  const sharedPoints = useRef(
    Array(5).fill(0).map((_, i) => ({
      x: useSharedValue(props.drawPoints[i]?.x ?? 0),
      y: useSharedValue(props.drawPoints[i]?.y ?? 0),
    })),
  ).current;

  useEffect(() => {
    props.drawPoints.forEach((p, i) => {
      if (sharedPoints[i]) {
        sharedPoints[i].x.value = p.x;
        sharedPoints[i].y.value = p.y;
      }
    });
  }, [props.drawPoints, sharedPoints]);

  const startPoints = useRef([]);

  const animatedPoints = useDerivedValue(() => sharedPoints.map((p) => `${p.x.value},${p.y.value}`).join(' '));

  const animatedPolygonProps = useAnimatedProps(() => ({
    points: animatedPoints.value,
  }));

  const ellipseBounds = useDerivedValue(() => {
    if (sharedPoints.length < 4) {
      return {
        cx: 0, cy: 0, rx: 0, ry: 0,
      };
    }
    const [p0, p1, p2, p3] = sharedPoints;
    const cx = (p0.x.value + p2.x.value) / 2;
    const cy = (p0.y.value + p2.y.value) / 2;
    const vectorA = { x: p1.x.value - p0.x.value, y: p1.y.value - p0.y.value };
    const vectorB = { x: p3.x.value - p0.x.value, y: p3.y.value - p0.y.value };
    const rx = Math.sqrt(vectorB.x ** 2 + vectorB.y ** 2) / 2;
    const ry = Math.sqrt(vectorA.x ** 2 + vectorA.y ** 2) / 2;
    return {
      cx, cy, rx, ry,
    };
  });

  const animatedEllipseProps = useAnimatedProps(() => ({
    cx: ellipseBounds.value.cx,
    cy: ellipseBounds.value.cy,
    rx: ellipseBounds.value.rx,
    ry: ellipseBounds.value.ry,
  }));

  const gesture = Gesture.Pan()
    .onBegin(() => {
      startPoints.current = sharedPoints.map((p) => ({
        x: p.x.value,
        y: p.y.value,
      }));
    })
    .onUpdate((e) => {
      sharedPoints.forEach((point, i) => {
        point.x.value = startPoints.current[i].x + e.translationX;
        point.y.value = startPoints.current[i].y + e.translationY;
      });
    })
    .onEnd(() => {
      const updated = sharedPoints.map((p, i) => ({
        x: p.x.value,
        y: p.y.value,
        number: i,
      }));
      props.setDrawPoints(updated);
      props.setRedoPoints([]);
    });

  const centerStyle = useAnimatedStyle(() => {
    const minX = Math.min(...sharedPoints.map((p) => p.x.value));
    const maxX = Math.max(...sharedPoints.map((p) => p.x.value));
    const minY = Math.min(...sharedPoints.map((p) => p.y.value));
    const maxY = Math.max(...sharedPoints.map((p) => p.y.value));
    return {
      position: 'absolute',
      left: (minX + maxX) / 2 - 10,
      top: (minY + maxY) / 2 - 10,
    };
  });

  return (
    <View style={StyleSheet.absoluteFillObject} pointerEvents="box-none">
      {sharedPoints.length > 0 && (
        <GestureDetector gesture={gesture}>
          <Animated.View style={[Style.grabPoint, centerStyle]}>
            <PressableStyled
              testID={isEllipse ? 'drag-drop-circle-btn' : 'drag-drop-rectangle-btn'}
            >
              <MoveIcon color="#fff" />
            </PressableStyled>
          </Animated.View>
        </GestureDetector>
      )}
      <Svg
        style={{
          zIndex: -1,
          width: '100%',
          height: '100%',
        }}
        pointerEvents="none"
      >
        {isEllipse ? (
          <AnimatedEllipse
            animatedProps={animatedEllipseProps}
            stroke={Colors.light.hereworksOrange500}
            strokeDasharray={[5, 5]}
            strokeWidth={2}
            fill="none"
          />
        ) : (
          <AnimatedPolygon
            animatedProps={animatedPolygonProps}
            stroke={Colors.light.hereworksOrange500}
            strokeDasharray={[5, 5]}
            strokeWidth={2}
            fill="none"
          />
        )}
      </Svg>
    </View>
  );
};

export default DrawRectGrab;
